/**
 * What-if changes as the steps of a change set, the vocabulary Claude's run_what_if and
 * apply_what_if take (cloud/src/mcp/plan-items.ts): each preset is an addition starting on a day, a
 * redirect sends a debt's payment on once it ends, a percent changes an item from a day (R24). The
 * same change set drives the comparison, a shared snapshot and "Apply to my plan".
 */
import { contract, core, formatDay, parseDay, schedule, type Day } from '@map-my-money/engine';
import { compareForecasts, itemEnd, runForecast, type Comparison, type Forecasted } from '@map-my-money/views';
import { applyChangeSet, type AppliedChanges, type PlanChangeSet } from '../../../cloud/src/mcp/plan-items.ts';
import type { Waypoint, WireAsset, WireChanges, WireGoal, WirePlan, WireTransaction } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { compactMoney, money, monthYear, percent as percentText } from './format.ts';
import { linePoints, loanPayment, num, valuesOn } from './model.ts';

export type PresetKind = 'raise' | 'side' | 'spendLess' | 'invest' | 'windfall' | 'child' | 'car';
export type ChangeKind = PresetKind | 'redirect' | 'percent';

export type Change =
  | { readonly id: string; readonly kind: PresetKind; readonly amount: number }
  /** When debt `from` makes its last payment, its payment goes into investment `to`. */
  | { readonly id: string; readonly kind: 'redirect'; readonly from: string; readonly to: string }
  /** Item `item` changes by `percent` from the first of a month (`starts`, YYYY-MM-DD), or from today when null. */
  | { readonly id: string; readonly kind: 'percent'; readonly item: string; readonly percent: number; readonly starts: string | null };

export type PresetChange = Extract<Change, { kind: PresetKind }>;
export type RedirectChange = Extract<Change, { kind: 'redirect' }>;
export type PercentChange = Extract<Change, { kind: 'percent' }>;

interface KindSpec {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly initial: number;
  readonly monthly: boolean;
  /** Effect on monthly cash: +1 frees money, −1 costs money, 0 moves it (investing). */
  readonly cashSign: 1 | -1 | 0;
  readonly tone: 'mint' | 'amber' | 'violet' | 'terracotta';
}

export const KINDS: Readonly<Record<PresetKind, KindSpec>> = {
  raise: { min: 0, max: 2000, step: 25, initial: 350, monthly: true, cashSign: 1, tone: 'mint' },
  side: { min: 0, max: 2000, step: 50, initial: 400, monthly: true, cashSign: 1, tone: 'mint' },
  spendLess: { min: 0, max: 1500, step: 25, initial: 200, monthly: true, cashSign: 1, tone: 'amber' },
  invest: { min: 0, max: 2000, step: 25, initial: 300, monthly: true, cashSign: 0, tone: 'violet' },
  windfall: { min: 0, max: 100000, step: 1000, initial: 20000, monthly: false, cashSign: 1, tone: 'mint' },
  child: { min: 0, max: 2000, step: 50, initial: 650, monthly: true, cashSign: -1, tone: 'amber' },
  car: { min: 0, max: 80000, step: 1000, initial: 25000, monthly: false, cashSign: -1, tone: 'terracotta' },
};

export const PERCENT_RANGE = { min: -50, max: 50, step: 1 } as const;

export const CAR_LOAN = { ratePercent: 7.9, termMonths: 48 } as const;


/** Presets take effect from the first of next month. */
export const changeStart = (today: Day): Day => core.addMonths(core.startOfMonth(today), 1);

/** The months a percent change can start from: the first of each of the next twelve, then in two, three and five years. */
export function startChoices(today: Day): string[] {
  const first = core.startOfMonth(today);
  return [...Array.from({ length: 12 }, (_, i) => i + 1), 24, 36, 60].map((months) => formatDay(core.addMonths(first, months)));
}

// MARK: What the plan offers

type Tx = WireTransaction;

const transactions = (wire: WirePlan): readonly Tx[] => wire.transactions ?? [];
const nameOf = (t: Tx) => t.label || t.id;
const running = (t: Tx, asOf: string) => t.isActive !== false && (t.endDate === undefined || t.endDate === null || t.endDate >= asOf);

export interface EndingDebt {
  readonly id: string;
  readonly label: string;
  /** The payment freed when it ends. */
  readonly payment: string;
  /** The first payment the redirect makes. */
  readonly next: Day;
}

/** Debts with a last payment still to come: the ones a redirect can start from. */
export function endingDebts(wire: WirePlan, today: Day): EndingDebt[] {
  const asOf = formatDay(today);
  return transactions(wire).flatMap((t) => {
    if (t.type !== 'debt' || t.isActive === false) return [];
    const end = itemEnd(wire, t.id, asOf);
    if (end === null || end.last < asOf) return [];
    return [{ id: t.id, label: nameOf(t), payment: valuesOn(t, end.last).amount ?? t.amount, next: parseDay(end.next) }];
  });
}

export const investments = (wire: WirePlan, today: Day): { id: string; label: string }[] =>
  transactions(wire).filter((t) => t.type === 'investment' && running(t, formatDay(today))).map((t) => ({ id: t.id, label: nameOf(t) }));

/** Incomes, expenses and investments that pay regularly and have not ended: what a percent change applies to. */
export const percentItems = (wire: WirePlan, today: Day): { id: string; label: string; type: Tx['type'] }[] =>
  transactions(wire)
    .filter((t) => t.type !== 'debt' && t.frequency !== 'oneTime' && running(t, formatDay(today)))
    .map((t) => ({ id: t.id, label: nameOf(t), type: t.type }));

let nextId = 1;
const freshId = (kind: ChangeKind) => `${kind}${nextId++}`;

export const makePreset = (kind: PresetKind, amount = KINDS[kind].initial): PresetChange => ({ id: freshId(kind), kind, amount });

/** A redirect from the debt that ends first into the first investment, or null when the plan has neither. */
export function makeRedirect(wire: WirePlan, today: Day): RedirectChange | null {
  const debt = [...endingDebts(wire, today)].sort((a, b) => a.next - b.next)[0];
  const into = investments(wire, today)[0];
  return debt && into ? { id: freshId('redirect'), kind: 'redirect', from: debt.id, to: into.id } : null;
}

/** −10% on the first expense (a cut), else +10% on the first item. */
export function makePercent(wire: WirePlan, today: Day): PercentChange | null {
  const items = percentItems(wire, today);
  const item = items.find((i) => i.type === 'expense') ?? items[0];
  return item ? { id: freshId('percent'), kind: 'percent', item: item.id, percent: item.type === 'expense' ? -10 : 10, starts: null } : null;
}

export function isActive(c: Change): boolean {
  if (c.kind === 'redirect') return true;
  if (c.kind === 'percent') return c.percent !== 0;
  return c.amount > 0;
}

// MARK: Words

const findTx = (wire: WirePlan, id: string) => transactions(wire).find((t) => t.id === id);

export function changeLabel(change: Change, today: Day, wire: WirePlan): string {
  switch (change.kind) {
    case 'raise':
      return t('whatIf.kind.raiseFrom', { date: monthYear(changeStart(today)) });
    case 'redirect': {
      const debt = findTx(wire, change.from);
      return t('whatIf.step.redirect', { debt: debt ? nameOf(debt) : change.from });
    }
    case 'percent': {
      const item = findTx(wire, change.item);
      return item ? nameOf(item) : change.item;
    }
    default:
      return t(`whatIf.kind.${change.kind}`);
  }
}

export function changeValueLabel(change: Change, wire: WirePlan, today: Day): string {
  if (change.kind === 'redirect') {
    const debt = endingDebts(wire, today).find((d) => d.id === change.from);
    return debt ? `+${money(Number(debt.payment))}${t('common.perMonth')}` : '';
  }
  if (change.kind === 'percent') return percentText(change.percent, { sign: true });
  const spec = KINDS[change.kind];
  if (!spec.monthly) return change.kind === 'car' ? compactMoney(change.amount) : `+${compactMoney(change.amount)}`;
  const sign = spec.cashSign < 0 || change.kind === 'spendLess' ? '−' : '+';
  return `${sign}${money(change.amount)}${t('common.perMonth')}`;
}

/** When a redirect or a percent change starts ("from Apr 2030", "from today"); presets say it in their label. */
export function changeWhen(change: Change, wire: WirePlan, today: Day): string | null {
  if (change.kind === 'redirect') {
    const debt = endingDebts(wire, today).find((d) => d.id === change.from);
    return debt ? t('whatIf.step.from', { date: monthYear(debt.next) }) : null;
  }
  if (change.kind === 'percent') return change.starts === null ? t('whatIf.step.fromToday') : t('whatIf.step.from', { date: monthYear(parseDay(change.starts)) });
  return null;
}

// MARK: The change set

export const carMonthlyPayment = (price: number) => loanPayment(price, CAR_LOAN.ratePercent, CAR_LOAN.termMonths);

const str = (n: number) => String(Math.round(n * 100) / 100);

type Addition = NonNullable<PlanChangeSet['add']>[number];

function presetAdditions(c: PresetChange, wire: WirePlan, start: string, id: string): Addition[] {
  const tx = (item: Omit<WireTransaction, 'startDate'>): Addition => ({ kind: 'transaction', item: { ...item, startDate: start }, from: start });
  switch (c.kind) {
    case 'raise':
      return [tx({ id, label: t('whatIf.kind.raise'), type: 'income', amount: str(c.amount), annualIncreasePercent: '0' })];
    case 'side':
      return [tx({ id, label: t('whatIf.kind.side'), type: 'income', amount: str(c.amount) })];
    case 'spendLess':
      return [tx({ id, label: t('whatIf.kind.spendingCut'), type: 'income', amount: str(c.amount) })];
    case 'invest':
      return [tx({ id, label: t('whatIf.kind.invest'), type: 'investment', amount: str(c.amount), initialBalance: '0', interestRate: wire.configuration.expectedReturnPercent })];
    case 'windfall':
      return [tx({ id, label: t('whatIf.kind.windfall'), type: 'income', amount: str(c.amount), frequency: 'oneTime' })];
    case 'child':
      return [tx({ id, label: t('whatIf.kind.child'), type: 'expense', amount: str(c.amount), annualIncreasePercent: '3' })];
    case 'car': {
      const asset: WireAsset = {
        id: `${id}-asset`, label: t('whatIf.kind.car'), purchasePrice: str(c.amount), purchaseDate: start,
        assetClass: 'vehicle', valueTrajectory: { kind: 'depreciating', preset: 'vehicle-standard' },
      };
      return [
        { kind: 'asset', item: { ...asset }, from: start },
        tx({
          id: `${id}-loan`, label: t('whatIf.kind.carLoan'), type: 'debt', amount: str(carMonthlyPayment(c.amount)),
          linkedAssetId: asset.id, debtType: 'fixedTerm', debtEntryMode: 'originalLoan', debtPrincipal: str(c.amount),
          debtTermMonths: CAR_LOAN.termMonths, interestRate: String(CAR_LOAN.ratePercent),
        }),
      ];
    }
  }
}

/** The change set Claude would send for these changes; `idSuffix` keeps added ids unique when they are saved. */
export function toChangeSet(changes: readonly Change[], wire: WirePlan, today: Day, options: { years?: number; idSuffix?: string } = {}): PlanChangeSet {
  const start = formatDay(changeStart(today));
  const add: Addition[] = [];
  const update: NonNullable<PlanChangeSet['update']>[number][] = [];
  const redirect: NonNullable<PlanChangeSet['redirect']>[number][] = [];
  for (const c of changes.filter(isActive)) {
    if (c.kind === 'redirect') redirect.push({ from: c.from, to: c.to, when: 'ends' });
    else if (c.kind === 'percent') update.push({ id: c.item, kind: 'transaction', by_percent: c.percent, ...(c.starts === null ? {} : { from: c.starts }) });
    else add.push(...presetAdditions(c, wire, start, `whatif-${c.id}${options.idSuffix ?? ''}`));
  }
  return {
    ...(add.length ? { add } : {}),
    ...(update.length ? { update } : {}),
    ...(redirect.length ? { redirect } : {}),
    ...(options.years === undefined ? {} : { timeframe_years: options.years }),
  };
}

/** Presets as plain additions, for the shared map's own adjustments. */
export function toWireChanges(changes: readonly PresetChange[], wire: WirePlan, today: Day): { transactions: WireTransaction[]; assets: WireAsset[] } {
  const add = toChangeSet(changes, wire, today).add ?? [];
  return {
    transactions: add.filter((a) => a.kind === 'transaction').map((a) => a.item as unknown as WireTransaction),
    assets: add.filter((a) => a.kind === 'asset').map((a) => a.item as unknown as WireAsset),
  };
}

/** The changes applied to the plan, or null when a step no longer fits it (an item since removed, say). */
export function applied(wire: WirePlan, changes: readonly Change[], today: Day, options: { years?: number; idSuffix?: string } = {}): AppliedChanges | null {
  try {
    return applyChangeSet(wire, toChangeSet(changes, wire, today, options), { today: formatDay(today) });
  } catch {
    return null;
  }
}

const monthlyOf = (t: Tx, amount: number) => (amount * schedule.annualOccurrences(contract.decodeFrequency(t.frequency))) / 12;

/** A percent change's amount before and after, on the day it starts, from the plan as changed. */
export function percentAmounts(change: PercentChange, wire: WirePlan, changed: WirePlan, today: Day): { before: number; after: number; item: Tx } | null {
  const before = findTx(wire, change.item);
  const after = transactions(changed).find((t) => t.id === change.item);
  if (!before || !after) return null;
  const on = change.starts ?? formatDay(today);
  return { before: Number(valuesOn(before, on).amount ?? before.amount), after: Number(valuesOn(after, on).amount ?? after.amount), item: after };
}

/** Net effect on monthly cash once every change has started (investing and redirects only move money, so they don't count). */
function extraPerMonth(changes: readonly Change[], wire: WirePlan, changed: WirePlan, today: Day): number {
  return changes.filter(isActive).reduce((total, c) => {
    if (c.kind === 'redirect') return total;
    if (c.kind === 'percent') {
      const amounts = percentAmounts(c, wire, changed, today);
      if (!amounts || amounts.item.type === 'investment') return total;
      const sign = amounts.item.type === 'income' ? 1 : -1;
      return total + sign * monthlyOf(amounts.item, amounts.after - amounts.before);
    }
    if (c.kind === 'car') return total - carMonthlyPayment(c.amount);
    const spec = KINDS[c.kind];
    return spec.monthly ? total + spec.cashSign * c.amount : total;
  }, 0);
}

/**
 * A shared snapshot takes additions only: the change set's new items as they are, and each percent
 * change as the difference it makes from its day (a cut as money kept, like "Spend less").
 */
function shareAdditions(changes: readonly Change[], wire: WirePlan, changed: WirePlan, today: Day): { transactions: WireTransaction[]; assets: WireAsset[] } {
  const known = new Set(transactions(wire).map((t) => t.id));
  const knownAssets = new Set((wire.assets ?? []).map((a) => a.id));
  const added = transactions(changed).filter((t) => !known.has(t.id));
  const deltas = changes.filter((c): c is PercentChange => c.kind === 'percent' && isActive(c)).flatMap((c): WireTransaction[] => {
    const amounts = percentAmounts(c, wire, changed, today);
    if (!amounts || amounts.after === amounts.before) return [];
    const { item } = amounts;
    const more = amounts.after > amounts.before;
    const type = item.type === 'expense' ? (more ? 'expense' : 'income') : more ? item.type : 'expense';
    return [{
      id: `whatif-${c.id}`, label: `${nameOf(item)} ${percentText(c.percent, { sign: true })}`, type, amount: str(Math.abs(amounts.after - amounts.before)),
      startDate: c.starts ?? formatDay(today), ...(item.frequency === undefined ? {} : { frequency: item.frequency }),
      ...(item.annualIncreasePercent === undefined ? {} : { annualIncreasePercent: item.annualIncreasePercent }),
      ...(type === 'investment' ? { initialBalance: '0', interestRate: item.interestRate ?? wire.configuration.expectedReturnPercent } : {}),
    }];
  });
  return { transactions: [...added, ...deltas], assets: (changed.assets ?? []).filter((a) => !knownAssets.has(a.id)) };
}

// MARK: The comparison

export interface ScenarioGoal {
  readonly goal: Pick<WireGoal, 'id' | 'label' | 'series'>;
  readonly baselineDate: Day | null;
  readonly modifiedDate: Day | null;
  /** Months sooner with the changes (negative: later); null unless both lines reach the goal. */
  readonly monthsFaster: number | null;
  readonly becomesAchievable: boolean;
}

export interface ScenarioView {
  readonly comparison: Comparison;
  readonly years: number;
  readonly baseline: readonly { date: Day; value: number }[];
  readonly modified: readonly { date: Day; value: number }[];
  readonly baselineFinal: number;
  readonly modifiedFinal: number;
  readonly impact: number;
  readonly goals: readonly ScenarioGoal[];
  readonly extraPerMonth: number;
  /** The plan with the changes, for reading what a step became. */
  readonly changed: WirePlan;
  /** Changes whose step no longer fits the plan; left out of the comparison. */
  readonly skipped: readonly string[];
  readonly wireChanges: WireChanges;
}

let baselineCache: { wire: WirePlan; waypoints: readonly Waypoint[]; asOf: string; years: number; forecast: Forecasted } | null = null;

/** The plan as it is: the same for every slider move, so it is run once per plan, day and horizon. */
function baselineForecast(wire: WirePlan, waypoints: readonly Waypoint[], asOf: string, years: number): Forecasted {
  const c = baselineCache;
  if (c && c.wire === wire && c.waypoints === waypoints && c.asOf === asOf && c.years === years) return c.forecast;
  const forecast = runForecast({ plan: wire, waypoints, asOf }, years);
  baselineCache = { wire, waypoints, asOf, years, forecast };
  return forecast;
}

/** Changes that apply on their own, then together; one that no longer fits the plan is skipped rather than blanking the screen. */
function applyFitting(wire: WirePlan, changes: readonly Change[], today: Day, years: number): { result: AppliedChanges; skipped: string[] } {
  const all = applied(wire, changes, today, { years });
  if (all) return { result: all, skipped: [] };
  const fitting = changes.filter((c) => applied(wire, [c], today) !== null);
  return { result: applied(wire, fitting, today, { years }) ?? applyChangeSet(wire, { timeframe_years: years }), skipped: changes.filter((c) => !fitting.includes(c)).map((c) => c.id) };
}

export function scenarioView(wire: WirePlan, waypoints: readonly Waypoint[], changes: readonly Change[], today: Day, years: number): ScenarioView {
  const asOf = formatDay(today);
  const { result, skipped } = applyFitting(wire, changes, today, years);
  const before = baselineForecast(wire, waypoints, asOf, years);
  const fitting = changes.filter((c) => isActive(c) && !skipped.includes(c.id));
  const after = fitting.length === 0 ? before : runForecast({ plan: result.plan, waypoints, asOf }, years);
  const comparison = compareForecasts(before, after);
  const line = (f: Forecasted) => linePoints({ forecast: f.forecast, today }).map((p) => ({ date: p.date, value: num(p.netWorth) }));
  const goalsById = new Map((wire.goals ?? []).map((g) => [g.id, g]));
  const day = (d: string | null) => (d === null ? null : parseDay(d));
  return {
    comparison,
    years,
    baseline: line(before),
    modified: line(after),
    baselineFinal: Number(comparison.final_net_worth_before),
    modifiedFinal: Number(comparison.final_net_worth_after),
    impact: Number(comparison.net_worth_change),
    goals: comparison.goals.map((g) => ({
      goal: { id: g.id, label: g.label, series: goalsById.get(g.id)?.series ?? 'netWorth' },
      baselineDate: day(g.before),
      modifiedDate: day(g.after),
      monthsFaster: g.months_sooner,
      becomesAchievable: g.becomes_reachable,
    })),
    extraPerMonth: extraPerMonth(fitting, wire, result.plan, today),
    changed: result.plan,
    skipped,
    wireChanges: { ...shareAdditions(fitting, wire, result.plan, today), timeframeYears: years },
  };
}

/** The plan with the changes made real, and each changed item's day for the write (R24). */
export function applyToPlan(wire: WirePlan, changes: readonly Change[], today: Day): { plan: WirePlan; changesFrom: Readonly<Record<string, string>> } | null {
  const result = applied(wire, changes, today, { idSuffix: `-${Date.now().toString(36)}` });
  return result && { plan: result.plan, changesFrom: result.changesFrom };
}

export function stepText(change: Change, today: Day, wire: WirePlan): string {
  if (change.kind === 'redirect') {
    const debt = endingDebts(wire, today).find((d) => d.id === change.from);
    const into = findTx(wire, change.to);
    if (debt && into) {
      return t('whatIf.step.redirectSummary', { debt: debt.label, amount: `${money(Number(debt.payment))}${t('common.perMonth')}`, investment: nameOf(into), date: monthYear(debt.next) });
    }
  }
  const when = changeWhen(change, wire, today);
  return [changeLabel(change, today, wire), changeValueLabel(change, wire, today), when].filter(Boolean).join(' ');
}

export const applySummary = (changes: readonly Change[], today: Day, wire: WirePlan): string =>
  t('plan.summaryText.whatIf', { changes: changes.filter(isActive).map((c) => stepText(c, today, wire)).join(', ') });
